import { repeat } from 'lodash-es';
import numeral from 'numeral';

type InputValue = string | number | null;

export function fNumber(number: InputValue) {
  return numeral(number).format();
}

export function fCurrency(number: InputValue, precision = 2) {
  const precisionFormat = precision ? `.${repeat('0', precision)}` : '';
  const format = number
    ? numeral(number).format(`$0,${precisionFormat}`)
    : '$0';

  return result(format, precisionFormat);
}

export function fPercent(number: InputValue) {
  const format = number ? numeral(Number(number) / 100).format('0.0%') : '';

  return result(format, '.0');
}

export function fShortenNumber(number: InputValue) {
  const format = number ? numeral(number).format('0.00a') : '';

  return result(format, '.00');
}

export function fData(number: InputValue) {
  const format = number ? numeral(number).format('0.0 b') : '';

  return result(format, '.0');
}

function result(format: string, key = '.00') {
  const isInteger = format.includes(key);

  return isInteger ? format.replace(key, '') : format;
}

export function toNumber(value?: any, defaultValue?: number) {
  const num = +value;

  if (!Number.isNaN(num)) return num;

  return defaultValue;
}

export function toFixedNumber(
  value: number,
  precision = 2,
  trimRightZeros = true
) {
  const val = value.toFixed(precision);
  return trimRightZeros ? val.replace(/\.0+$/, '') : val;
}

const HUMAN_FILE_SIZE_VALUES = ['', 'K', 'M', 'G', 'T'];
export function fHumanReadableNumber(
  size = 0,
  decimalPlaces = 1,
  separator: '' | ' ' = ''
) {
  if (!size) return '0';

  const isNegative = size < 0;
  size = Math.abs(size);

  const val = Math.max(0, Math.floor(Math.log(size) / Math.log(1000)));
  const maxIndex = HUMAN_FILE_SIZE_VALUES.length - 1;
  const safeVal = Math.min(val, maxIndex);

  const res = (size / 1000 ** safeVal)
    .toFixed(decimalPlaces)
    .replace(/\.0+$/, '')
    .concat(separator)
    .concat(HUMAN_FILE_SIZE_VALUES[safeVal])
    .trim();

  return isNegative ? '-' + res : res;
}

const HUMAN_CURRENCY_VALUES = ['', 'K', 'M', 'B', 'T'];
export function fHumanReadableCurrency(
  size = 0,
  decimalPlaces = 1,
  separator: '' | ' ' = ''
) {
  if (!size) return 0;
  const val = Math.floor(Math.log(size) / Math.log(1000));
  return `$${(size / 1000 ** val)
    .toFixed(decimalPlaces)
    .replace(/\.0+$/, '')
    .concat(separator)
    .concat(HUMAN_CURRENCY_VALUES.at(val) || '')
    .trim()}`;
}

export const toPercent = (
  number1?: number,
  number2?: number,
  reverse = false
) => {
  const num1 = number1 ?? 0;
  const num2 = number2 ?? 1;

  if (num2 === 0) return 0;

  return reverse ? (num2 / num1) * 100 : (num1 / num2) * 100;
};

export const isNumeric = (value: string) => {
  return /^-?\d+$/.test(value);
};

export const formatPriceNumber = (number: number) => {
  return number?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export function fCredit(number: number) {
  return toFixedNumber(number, 1).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function roundNumber(
  value: number | string | undefined,
  decimals = 0
): number {
  if (value === undefined) return NaN;

  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return NaN;

  const factor = 10 ** decimals;
  return Math.round(num * factor) / factor;
}

export function fOrdinal(num: number): string {
  return numeral(num).format('0o');
}

export function fAbsoluteNumber(value: InputValue): number | null {
  if (value === null || value === undefined) return value;
  return Math.abs(Number(value));
}
